<template>
    <div class="bubble-panel">
        <div ref="field" class="bubble-field" :style="{ height: `${fieldHeight}px` }">
            <!-- Buttons rather than divs: these are the only way to reach the
                 per-game numbers, so they have to be tabbable and have an
                 accessible name. -->
            <button
                v-for="bubble in layout.bubbles"
                :key="bubble.id"
                ref="bubbles"
                type="button"
                :data-bubble-id="bubble.id"
                class="bubble"
                :class="{ 'is-selected': bubble.id === selected.id }"
                :style="styleFor(bubble)"
                :aria-pressed="bubble.id === selected.id"
                :aria-label="`${bubble.name}: ${bubble.games} played, ${bubble.wins} won`"
                @click="select(bubble.id)">
                <!-- The name runs around the inside edge. Decorative only —
                     the button already carries the name in its aria-label,
                     and a screen reader reading "CAH • CAH • CAH • " would be
                     worse than useless. -->
                <svg
                    v-if="bubble.ring"
                    class="bubble-ring"
                    :viewBox="`0 0 ${bubble.r * 2} ${bubble.r * 2}`"
                    aria-hidden="true"
                    focusable="false">
                    <path :id="ringPathId(bubble)" :d="bubble.ring.path" fill="none" />
                    <text :font-size="bubble.ring.fontSize">
                        <textPath
                            :href="`#${ringPathId(bubble)}`"
                            :textLength="bubble.ring.length"
                            lengthAdjust="spacing">{{ bubble.ring.text }}</textPath>
                    </text>
                </svg>
                <span class="bubble-count">{{ bubble.games }}</span>
            </button>

            <p v-if="!layout.bubbles.length" class="bubble-empty">
                No games played yet — start one with <code>/play</code> in any server Gamebot is in.
            </p>
        </div>

        <!-- The numbers the bubbles deliberately don't show.
             
             The bubbles carry one quantity — plays, as both size and label —
             so wins and win rate live here or nowhere. This is why the panel
             can drop the "size = played · number = wins" caption it used to
             carry: the strip says what a bubble means by showing it, for the
             game you tapped, instead of asking you to hold a key in your
             head.

             Starts on the most-played game so the panel is never blank, and
             follows whichever bubble you tap. -->
        <div v-if="selected" class="bubble-detail" aria-live="polite">
            <span class="detail-name">
                <span class="detail-dot" :style="{ backgroundColor: colorFor(selected.id) }"></span>
                {{ selected.name }}
            </span>
            <span class="detail-stats">
                <span class="detail-stat"><b>{{ selected.games }}</b> played</span>
                <span class="detail-stat"><b>{{ selected.wins }}</b> won</span>
                <span class="detail-stat"><b>{{ selected.winRatePercent }}</b> win rate</span>
            </span>
        </div>
    </div>
</template>

<script>
import { colorsForGame } from '../../globals/games.js'
import { bubbleLayout, debounce, nudgeTilt, ringLayout, tiltFor } from '../../util/profile.js'
import { PHYSICS, advance, applyJump, createBodies, settleBodies, settledHeight } from '../../util/bubblePhysics.js'

/**
 * Air above the settled pile — the distance the cluster falls on arrival, and
 * the room it has to bounce into when the jar is shaken.
 */
const DROP_ROOM = 40

/**
 * The games you play, as a jar of bubbles you can shake.
 *
 * One thing is encoded, two ways: the **size** of a bubble and the **number**
 * in it are both how many times you've won that game. They used to be two
 * different quantities — size from plays, the number from wins — which meant
 * the biggest circle on the page could be showing off a game you mostly lost.
 * This replaced a five-column table, which was accurate and nobody wanted to
 * look at. The exact figures still exist — they're in the tooltip and the
 * detail strip — but they're no longer what the panel is for.
 *
 * Colours are the per-game ones from globals/games.js, the same associations
 * the Overview tab's "most played" circles use, at full strength and flat:
 * no gradient, no shadow.
 *
 * The bubbles have gravity and bounce off each other (util/bubblePhysics.js).
 * They drop once, when the panel first scrolls into view, and then stay put —
 * there is no ongoing reaction to scrolling. Tapping one makes it hop.
 *
 * Under `prefers-reduced-motion` nothing animates: the pile is settled
 * synchronously on mount and painted once, so the reader sees the same
 * arrangement everyone else ends up with, just without watching it arrive.
 * Tapping still selects and still updates the numbers; it just doesn't hop.
 *
 * Three things keep the motion from being a battery drain:
 *
 *  - the loop stops the moment everything settles, and restarts on a nudge;
 *  - it never runs while the panel is off screen;
 *  - positions are written straight to each element's transform, so a frame
 *    costs one style write per bubble rather than a Vue re-render.
 */
export default {
    name: 'GameBubbles',
    props: {
        games: { type: Array, default: () => [] },
    },
    data() {
        return {
            // Measured rather than assumed: the layout is a function of the
            // strip's real width, and this panel is edge to edge on a phone.
            width: 0,
            // Which game the detail strip is showing. Null means "the biggest
            // one", resolved in `selected` — storing the id up front would go
            // stale the moment the stats change.
            selectedId: null,
        }
    },
    computed: {
        layout() {
            const width = this.width || 600
            const packed = bubbleLayout(this.games, {
                width: Math.max(120, width),
                // A phone gets smaller bubbles, or one heavy game fills the
                // whole strip and there is nothing to compare it against.
                maxRadius: width < 480 ? 52 : 72,
                minRadius: width < 480 ? 20 : 26,
            })

            return {
                ...packed,
                bubbles: packed.bubbles.map(bubble => ({
                    ...bubble,
                    ring: ringLayout(bubble),
                    // A small lean, stable per game. Applied to the whole
                    // bubble rather than just the text: a circle is identical
                    // under rotation, so tilting it costs nothing physically
                    // and the number leans with the ring.
                    tilt: tiltFor(bubble.id),
                })),
            }
        },

        /**
         * Sized to the settled pile, not to the packed cluster.
         *
         * The cluster is a round blob and the pile is flat and wide, so
         * `layout.height` overshoots badly — about a third of the panel was
         * dead air on a phone. The bubbles start from the packed positions
         * and the walls clamp anything above the box on the first step, which
         * is invisible because they're falling anyway.
         */
        fieldHeight() {
            return settledHeight(this.layout.bubbles, this.width || 600) + DROP_ROOM
        },

        /**
         * The game the detail strip describes.
         *
         * Falls back to the largest bubble, which is also what `bubbleLayout`
         * sorts first. The fallback covers two cases: nothing tapped yet, and
         * a previously tapped game that has left the list.
         */
        selected() {
            const bubbles = this.layout.bubbles
            if (!bubbles.length) return null
            const chosen = bubbles.find(b => b.id === this.selectedId) || bubbles[0]

            // Built here rather than in the template: Vue templates can't
            // reach `Math`.
            return {
                ...chosen,
                winRatePercent: `${Math.round(chosen.winRate)}%`,
            }
        },

        motionAllowed() {
            if (typeof window === 'undefined' || !window.matchMedia) return true
            return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        },
    },
    watch: {
        // A resize or a change of stats invalidates every position.
        layout() { this.$nextTick(this.reset) },
    },
    methods: {
        colorFor(id) { return colorsForGame(id).background },

        /** Unique per component instance — ids are global to the document. */
        ringPathId(bubble) { return `bubble-ring-${this._uid}-${bubble.id}` },

        /**
         * Everything that doesn't change frame to frame. Position is *not*
         * here — it's a transform written straight to the element, so a
         * bouncing bubble never touches Vue's render.
         */
        styleFor(bubble) {
            const colors = colorsForGame(bubble.id)
            return {
                width: `${bubble.r * 2}px`,
                height: `${bubble.r * 2}px`,
                backgroundColor: colors.background,
                color: colors.text,
                fontSize: `${Math.max(0.7, bubble.r / 34)}rem`,
            }
        },


        /**
         * Shows a game's numbers in the strip below, hops its bubble, and
         * knocks it a little further askew.
         *
         * The hop is deliberately small — a bounce in place, not a launch.
         * An earlier version threw the bubble hard enough to rearrange the
         * pile, which turned comparing two games into whack-a-mole; the
         * numbers you tapped for have to stay reachable.
         */
        select(id) {
            this.selectedId = id
            if (!this.motionAllowed) return

            // Seeded with the tap count so consecutive taps on the same
            // bubble don't all nudge it by the same angle.
            this.tapCount += 1
            this.tiltTargetById[id] = nudgeTilt(
                this.tiltById[id] || 0,
                `${id}:${this.tapCount}`,
            )

            const bounds = { width: this.width, height: this.fieldHeight }
            applyJump(this.bodies, id, bounds)
            this.wake()
        },

        /**
         * Eases each bubble towards its target angle.
         *
         * Framerate-independent easing rather than a CSS transition: the
         * transform is rewritten every frame by the solver, and a transition
         * on it would fight the physics a frame behind.
         *
         * @returns {boolean} whether any bubble is still turning — the loop
         *   has to keep running for the rotation even after the pile settles.
         */
        stepTilts(seconds) {
            let turning = false

            for (const id of Object.keys(this.tiltById)) {
                const target = this.tiltTargetById[id]
                if (target === undefined) continue

                const difference = target - this.tiltById[id]
                if (Math.abs(difference) < 0.05) {
                    this.tiltById[id] = target
                    continue
                }

                this.tiltById[id] += difference * Math.min(1, seconds * 9)
                turning = true
            }

            return turning
        },

        measure() {
            const field = this.$refs.field
            if (field) this.width = field.clientWidth
        },

        /**
         * Puts the bodies back at their packed positions.
         *
         * Deliberately does *not* start the loop: the drop is held until the
         * panel is actually on screen, so someone who lands further down the
         * page still sees it happen rather than finding a pile that fell
         * while they weren't looking.
         */
        reset() {
            this.bodies = createBodies(this.layout.bubbles)
            this.bodyById = this.bodies.reduce((map, body) => {
                map[body.id] = body
                return map
            }, {})
            // Current angles, and where each is heading. A tap moves the
            // target; stepTilts() walks the current angle towards it.
            this.tiltById = this.layout.bubbles.reduce((map, bubble) => {
                map[bubble.id] = bubble.tilt
                return map
            }, {})
            this.tiltTargetById = { ...this.tiltById }

            if (!this.motionAllowed) {
                // Show where the bubbles end up rather than where they start.
                // Painting the packed positions instead would leave them
                // hovering in an arrangement gravity was meant to resolve —
                // and the bottom one half out of the panel.
                settleBodies(this.bodies, { width: this.width, height: this.fieldHeight })
                this.paint()
                return
            }

            this.paint()
            if (this.visible) this.wake()
        },

        paint() {
            const elements = this.$refs.bubbles
            if (!elements) return

            // Matched by id, not by index. A `ref` inside `v-for` collects
            // elements in *mount* order, which Vue does not guarantee to
            // match the array's order — and when the two disagree, every
            // bubble is painted at some other bubble's position.
            for (const el of elements) {
                const body = this.bodyById[el.dataset.bubbleId]
                if (!body) continue
                // translate3d rather than left/top: this runs every frame, and
                // only a transform stays off the layout path.
                // The tilt is presentation, not physics, so it lives beside
                // the bodies rather than on them — bubblePhysics has no
                // business knowing which way a circle leans.
                const tilt = this.tiltById[el.dataset.bubbleId] || 0
                el.style.transform =
                    `translate3d(${(body.x - body.r).toFixed(2)}px, ${(body.y - body.r).toFixed(2)}px, 0)`
                    + ` rotate(${tilt.toFixed(2)}deg)`
            }
        },

        /**
         * Runs the simulation on a fixed timestep, accumulating real elapsed
         * time. A variable step would let a dropped frame tunnel a fast bubble
         * clean through the floor.
         */
        frame(now) {
            this.rafId = null

            const elapsed = this.lastFrame === null
                ? PHYSICS.timestep
                : (now - this.lastFrame) / 1000
            this.lastFrame = now

            const bounds = { width: this.width, height: this.fieldHeight }
            const result = advance(this.bodies, bounds, this.pending, elapsed)
            this.pending = result.pending
            const turning = this.stepTilts(elapsed)

            if (result.stepped || turning) this.paint()

            // Keep going if anything moved, if a bubble is still turning, or
            // if this frame was too short to run a step at all. A 120Hz frame
            // is 8.3ms against a 16.7ms timestep, so every other frame
            // legitimately does no work — treating that as "settled" froze
            // the pile in mid-air.
            if (result.moving || turning || !result.stepped) this.schedule()
            else this.lastFrame = null
        },

        schedule() {
            if (this.rafId !== null || !this.visible || !this.motionAllowed) return
            this.rafId = window.requestAnimationFrame(this.frame)
        },

        /** Something disturbed the pile — start asking for frames again. */
        wake() {
            this.lastFrame = null
            this.schedule()
        },

    },
    created() {
        // Deliberately not in data(): these change up to 60 times a second and
        // nothing renders from them, so making them reactive would buy a
        // dependency-tracking pass per frame for no benefit.
        this.bodies = []
        this.bodyById = {}
        this.tiltById = {}
        this.tiltTargetById = {}
        this.tapCount = 0
        this.rafId = null
        this.lastFrame = null
        this.pending = 0
        this.visible = true
        this.observer = null
        this.onResize = null
    },
    mounted() {
        this.measure()
        this.onResize = debounce(this.measure, 150)
        window.addEventListener('resize', this.onResize, { passive: true })

        // Two jobs: hold the drop until the panel is on screen, and never
        // simulate while it isn't. Without the observer we can't tell, so the
        // fallback is to assume visible and drop immediately.
        if (window.IntersectionObserver) {
            this.visible = false
            this.observer = new IntersectionObserver(entries => {
                this.visible = entries[entries.length - 1].isIntersecting
                if (this.visible) this.wake()
            })
            this.observer.observe(this.$refs.field)
        }

        this.reset()
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        if (this.onResize) this.onResize.cancel()
        if (this.rafId !== null) window.cancelAnimationFrame(this.rafId)
        if (this.observer) this.observer.disconnect()
    },
}
</script>

<style scoped lang="scss">
.bubble-field {
    position: relative;
    width: 100%;
    // Bodies are clamped to these bounds, so nothing should reach the edge —
    // this is insurance against a resize landing mid-frame.
    overflow: hidden;

    // No double-tap zoom on a phone.
    //
    // The bubbles invite repeated tapping — that is the whole interaction —
    // and two taps in quick succession is also the browser's gesture for
    // zooming into a block. So shaking the jar zoomed the page instead, and
    // every tap carried the ~300ms delay the browser waits to find out which
    // one you meant.
    //
    // `manipulation` keeps panning and pinch-zoom, and drops only the
    // double-tap gesture. That distinction matters: `user-scalable=no` or
    // `touch-action: none` would take pinch-zoom away from everyone, which is
    // somebody's only way to read the page.
    touch-action: manipulation;
}

.bubble {
    position: absolute;
    top: 0;
    left: 0;
    border: 0;
    padding: 0;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    line-height: 1;
    text-align: center;
    cursor: pointer;
    user-select: none;

    // Selection is carried by contrast, not by a ring: the deselected
    // bubbles fade their contents while the chosen one stays at full
    // strength. An outline on top of a tilted circle read as a wobble.
    > * {
        opacity: 0.5;
        transition: opacity 0.18s ease;

        @media (prefers-reduced-motion: reduce) {
            transition: none;
        }
    }

    &.is-selected > *,
    &:hover > * {
        opacity: 1;
    }

    // Keyboard users get something, since hover and tilt can't help them.
    &:focus-visible {
        outline: 2px dashed currentColor;
        outline-offset: 4px;
    }
    // Flat fill only — no gradient, no shadow. The game colour is the point,
    // and anything layered over it dulls it.
    //
    // No transition either: every position comes from the solver, and a
    // transition would fight it a frame behind.
}

.bubble-count {
    font-size: 1.5em;
    font-weight: 900;
}

// The name, curved around the inside edge. Sized in SVG user units, which
// map 1:1 to px here, so the band is the same thickness on every bubble
// regardless of how big the circle is.
.bubble-ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    text {
        fill: currentColor;
        font-weight: bold;
        letter-spacing: 0.06em;
        // Small caps territory — the ring is decoration, and at 9px the
        // reader is getting shape and colour more than words.
        text-transform: uppercase;
    }
}

.bubble-empty {
    font-size: 0.85rem;
    opacity: 0.7;
}

// Detail strip





.bubble-detail {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35rem 0.85rem;
    margin-top: 0.6rem;
    padding-top: 0.6rem;
    border-top: 1px solid rgba(127, 127, 127, 0.18);
    font-size: 0.85rem;
}

.detail-name {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-weight: bold;
}

.detail-dot {
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    flex: 0 0 auto;
}

.detail-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem 0.85rem;
    opacity: 0.8;
}

.detail-stat {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

</style>
